<template>
  <div class="login-wrap">
    <!-- Brand -->
    <div class="brand">
      <img :src="logoUrl" alt="Edda Home" class="brand__logo" />
      <div class="brand__text">
        <h1 class="brand__title">Edda Home CRM</h1>
        <p class="brand__sub">Manage sales, operations & production</p>
      </div>
    </div>

    <!-- Form Card -->
    <div class="card login-card">
      <label class="helper">Employee ID</label>
      <input
        class="input"
        v-model.trim="form.id"
        placeholder="e.g., A-ADM-0001"
        autocomplete="username"
        @keyup.enter="doLogin"
      />

      <label class="helper label--stack">Password</label>
      <div class="password-row">
        <input
          :type="showPwd ? 'text' : 'password'"
          class="input"
          v-model="form.password"
          autocomplete="current-password"
          @keyup.enter="doLogin"
          @keydown="onPwdKey"
          @keyup="onPwdKey"
        />
        <button
          class="btn secondary toggle-btn"
          type="button"
          :aria-pressed="showPwd ? 'true' : 'false'"
          @click="showPwd = !showPwd"
        >
          {{ showPwd ? 'Hide' : 'Show' }}
        </button>
      </div>

      <div v-if="capsLockOn" class="helper warn">Caps Lock is ON</div>

      <div class="row row--meta">
        <label class="remember">
          <input type="checkbox" v-model="rememberMe" />
          <span>Remember me</span>
        </label>

        <a class="link" @click.prevent="onForgot">Forgot password?</a>
      </div>

      <button class="btn primary action" :disabled="busy" @click="doLogin">
        <span v-if="busy" class="spinner" aria-hidden="true"></span>
        {{ busy ? 'Signing in…' : 'Sign in' }}
      </button>

      <div v-if="error" class="helper error">{{ error }}</div>
    </div>

    <footer class="foot">
      <span class="helper">© {{ year }} Edda Home. All rights reserved.</span>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Login',
  data() {
    return {
      form: { id: '', password: '' },
      showPwd: false,
      rememberMe: true,
      capsLockOn: false,
      busy: false,
      error: '',
      logoUrl: require('@/assets/final_logo.jpeg')
    };
  },
  computed: {
    year() {
      return new Date().getFullYear();
    }
  },
  methods: {
    onPwdKey(e) {
      try {
        this.capsLockOn = !!(e.getModifierState && e.getModifierState('CapsLock'));
      } catch (err) {}
    },
    onForgot() {
      this.error = 'If you forgot your password, please contact your administrator.';
      var self = this;
      setTimeout(function() {
        self.error = '';
      }, 4000);
    },
    redirectAfterLogin() {
      // honor ?redirect= set by router guard
      var redirect = this.$route && this.$route.query && this.$route.query.redirect;
      if (typeof redirect === 'string' && redirect.startsWith('/portal/')) {
        this.$router.replace(redirect);
        return;
      }

      var u = this.$store.getters['auth/currentUser'] || {};
      var r = u.role;

      if (r === 'rm_store')   return this.$router.replace({ name: 'ops-rm-dashboard' });
      if (r === 'finance')    return this.$router.replace({ name: 'ops-finance' });
      if (r === 'dispatch')   return this.$router.replace({ name: 'ops-dispatch' });
      if (r === 'installer')  return this.$router.replace({ name: 'ops-installer' });

      if (r === 'branch_manager' || r === 'branch_admin' || r === 'super_admin')
        return this.$router.replace({ name: 'admin-crm' });

      return this.$router.replace({ name: 'sales-dashboard' });
    },

    async doLogin() {
      if (this.busy) return;
      this.error = '';
      if (!this.form.id) {
        this.error = 'Enter your Employee ID';
        return;
      }
      if (!this.form.password) {
        this.error = 'Enter your password';
        return;
      }

      this.busy = true;
      try {
        if (this.rememberMe) {
          localStorage.setItem('edda_last_id', this.form.id);
        } else {
          localStorage.removeItem('edda_last_id');
        }

        await this.$store.dispatch('auth/login', {
          id: this.form.id,
          password: this.form.password
        });
        this.redirectAfterLogin();
      } catch (e) {
        var msg = 'Invalid ID or password';
        if (e && e.response && e.response.data) {
          var d = e.response.data;
          if (d.error) msg = d.error;
          else if (d.message) msg = d.message;
        }
        this.error = msg;
      } finally {
        this.busy = false;
      }
    }
  },
  created() {
    // Pre-fill ID if remembered
    try {
      var last = localStorage.getItem('edda_last_id');
      if (last) this.form.id = last;
    } catch (err) {}

    var self = this;
    this.$store
      .dispatch('auth/hydrate')
      .then(function() {
        var authed = self.$store.getters['auth/isAuthenticated'];
        if (authed) self.redirectAfterLogin();
      })
      .catch(function() {});
  }
};
</script>

<style scoped>
/* Background */
.login-wrap {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  background-image: url("https://www.transparenttextures.com/patterns/arches.png");
  /* background-blend-mode: soft-light; */
  font-family: 'Inter', 'Segoe UI', sans-serif;
  padding: 32px 16px;
}



/* Brand */
.brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
  text-align: center;
}
.brand__logo {
  width: 188px;
  height: 188px;
  object-fit: contain;
  border-radius: 18px;
  background: #fff;
  padding: 10px;
  box-shadow: 0 6px 24px rgba(255, 0, 0, 0.2);
  border: 2px solid #e74c3c;
}
.brand__title {
  margin: 14px 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #E30613;
}
.brand__sub {
  margin: 0;
  font-size: 14px;
  color: #666;
  letter-spacing: 0.2px;
}

/* Card */
.login-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  padding: 22px 24px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}
.login-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

/* Inputs */
.input {
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.25s ease;
}
.input:hover {
  border-color: #d0d7de;
  background: rgba(255, 255, 255, 0.95);
}
.input:focus {
  border-color: #E30613;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.15);
  outline: none;
}


.helper {
  font-size: 13px;
  color: #666;
}
.label--stack {
  margin-top: 14px;
}

/* Password row */
.password-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
}
.toggle-btn {
  height: 42px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #f9f9f9;
  cursor: pointer;
}
.toggle-btn:hover {
  background: #fff;
  border-color: #ccc;
}

/* Buttons */
.btn.primary {
  background: #E30613;
  color: #fff;
  border: none;
  height: 44px;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.btn.primary:hover {
  background: #E30613;
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
}
.btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: -3px;
  animation: spin 700ms linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Meta + messages */
.row--meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0 12px;
}
.remember {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}
.link {
  cursor: pointer;
  text-decoration: none;
  color: #E30613;
  font-weight: 500;
}
.link:hover {
  text-decoration: underline;
}

.error {
  color: #E30613;
  margin-top: 10px;
  text-align: center;
}
.warn {
  color: #d9822b;
  margin-top: 6px;
  text-align: center;
}
.foot {
  margin-top: 24px;
  font-size: 12px;
  color: #888;
  text-align: center;
}
</style>

